import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { HelicopterIcon } from '@rsos/assets/icons';
import { useFlag } from '@rsos/flags';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import {
  resetSelectedAirAmbulance,
  setFormPageToShow,
} from '@rsos/reduxDataStore';
import {
  setSelectedTool,
  setSelectedTab,
  expand,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { AIR_AMBULANCE_TOOL } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import {
  setCardsToRender,
  closeCard,
  AIR_AMBULANCE_CARD,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import {
  trackRequestAirAmbulanceClick,
  AIR_AMBULANCE_SOURCE_BUTTON,
} from '../../../../gainsightTrackings/airAmbulanceTrackings';
import useHasAccessToUniteEventPane from '../../../../hooks/useHasAccessToUniteEventPane';
import { FORM_PAGES } from '../../../AirAmbulanceForm/AirAmbulanceForm/constants';
import { ActionBarItem } from '../ActionBarItem';

const TOOLS_TAB_ID = 'tools';

const AirAmbulanceActionBarItem = () => {
  const dispatch = useDispatch();

  const hasAccessToAirAmbulance = useHasAccessToAirAmbulance();
  const hasAccessToUniteEventPane = useHasAccessToUniteEventPane();
  const hasAirAmbulanceEnabled =
    hasAccessToAirAmbulance && hasAccessToUniteEventPane;

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const { cards } = useSelector(state => state.mapCard);
  const isAirAmbulanceCardOpened = useMemo(
    () => cards[AIR_AMBULANCE_CARD],
    [cards],
  );
  const callQueue = useSelector(state => state.emergencyCalls.callQueue);
  const hasLocationToRender = !!getMostRecentLoc(callQueue[selectedCallerID]);

  const handleClick = async () => {
    // Check if there's a selected call and log its information
    if (hasLocationToRender) {
      dispatch(setSelectedTab(TOOLS_TAB_ID));
      dispatch(setSelectedTool({ tool: AIR_AMBULANCE_TOOL }));
      dispatch(expand());
    } else if (isAirAmbulanceCardOpened) {
      dispatch(closeCard(AIR_AMBULANCE_CARD));
    } else if (!isAirAmbulanceCardOpened) {
      await dispatch(resetSelectedAirAmbulance());
      dispatch(setFormPageToShow(FORM_PAGES.FORM));
      dispatch(setCardsToRender(AIR_AMBULANCE_CARD));
    }
    trackRequestAirAmbulanceClick({
      sourceButton: AIR_AMBULANCE_SOURCE_BUTTON.ACTION_BAR,
    });
  };

  const tooltipText = hasLocationToRender
    ? `Request Air Ambulance for ${selectedCallerID}`
    : 'Request Air Ambulance';

  return (
    <ActionBarItem
      iconWidth="18px"
      onClick={handleClick}
      shouldRender={hasAirAmbulanceEnabled}
      tooltipText={tooltipText}
      isActive={isAirAmbulanceCardOpened}
      iconDataName="air-ambulance-action-bar-item-icon"
    >
      <HelicopterIcon />
    </ActionBarItem>
  );
};

export default AirAmbulanceActionBarItem;
